import {
  useBackgroundQuery as useApolloBackgroundQuery,
  useQueryRefHandlers,
  useReadQuery as useApolloReadQuery,
  useSuspenseQuery as useApolloSuspenseQuery,
  useQuery as useApolloQuery,
  type QueryRef,
  type UseReadQueryResult,
  type DocumentNode,
  type OperationVariables,
  type BackgroundQueryHookOptions,
  type SuspenseQueryHookOptions,
  type TypedDocumentNode,
  type UseBackgroundQueryResult,
  type QueryHookOptions,
} from '@apollo/client'
import debounce from 'lodash/debounce'
import { startTransition, useEffect, useMemo } from 'react'
import { getLiveKeysOfQuery } from '@/lib/graphql/get-live-keys-of-query'
import createSubscription from '@/lib/socket/create-subscription'
import type { RefetchFunction } from '@apollo/client/react/hooks/useSuspenseQuery'

const useLiveSubscription = <TData>({
  data,
  refetch,
}: {
  data: TData
  refetch: RefetchFunction<TData, object>
}) => {
  const liveUpdates = useMemo(() => {
    return createSubscription(
      debounce(
        () =>
          startTransition(async () => {
            await refetch()
          }),
        100
      )
    )
  }, [refetch])

  const liveKeys = useMemo(() => {
    const isDataEmpty = !data || Object.keys(data).length === 0
    if (isDataEmpty) {
      return []
    }
    return getLiveKeysOfQuery(data as object)
  }, [data])

  useEffect(() => {
    if (liveKeys.length) {
      liveUpdates.setLiveKeys(liveKeys)
    }
  }, [liveUpdates, liveKeys])

  useEffect(() => {
    return () => {
      liveUpdates.unsubscribe()
    }
  }, [liveUpdates])
}

type BackgroundQueryOptions<
  TData,
  TVariables extends OperationVariables,
> = Omit<BackgroundQueryHookOptions<TData>, 'variables'> & {
  variables?: TVariables
}

export const useBackgroundQuery = (<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: BackgroundQueryOptions<TData, TVariables>
): [
  QueryRef<TData, TVariables>,
  UseBackgroundQueryResult<TData, TVariables>,
] => {
  return useApolloBackgroundQuery<TData, TVariables>(query, options)
}) as unknown as typeof useApolloBackgroundQuery

export const useReadQuery = (<TData>(
  queryRef: QueryRef<TData>
): UseReadQueryResult<TData> => {
  const result = useApolloReadQuery<TData>(queryRef)
  const { refetch } = useQueryRefHandlers<TData>(queryRef)

  useLiveSubscription({
    data: result.data,
    refetch,
  })

  return result
}) as unknown as typeof useApolloReadQuery

type SuspenseQueryOptions<TData, TVariables extends OperationVariables> = Omit<
  SuspenseQueryHookOptions<TData>,
  'variables'
> & {
  variables?: TVariables
}

export const useSuspenseQuery = (<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: SuspenseQueryOptions<TData, TVariables>
) => {
  const result = useApolloSuspenseQuery<TData, TVariables>(query, options)

  useLiveSubscription({
    data: result.data,
    refetch: result.refetch,
  })

  return result
}) as unknown as typeof useApolloSuspenseQuery

export const useQuery = (<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>
) => {
  const result = useApolloQuery<TData, TVariables>(query, options)

  useLiveSubscription({
    data: result.data,
    refetch: result.refetch,
  })

  return result
}) as unknown as typeof useApolloQuery
